import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { CreateAgendaItemDialogComponent } from './create-agenda-item-dialog/create-agenda-item-dialog.component';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-agenda-items',
  templateUrl: './agenda-items.component.html',
  styleUrls: ['./agenda-items.component.scss']
})
export class AgendaItemsComponent implements OnInit {
  AppConstants = AppConstants;
  agendaItems: any[] = [];
  tableColumns: TableColumn[] = [];
  private isFetching: boolean = false;
  private meetingsCache: any[] | null = null;

  constructor(
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private _router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.initializeTableColumns();
    // Prevent duplicate calls if component is initialized multiple times
    if (!this.isFetching) {
      this.fetchAgendaItems();
    }
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View'
      },
      {
        iconPath: 'assets/icons/edit-icon.svg',
        action: 'edit',
        title: 'Edit'
      }
    ];

    this.tableColumns = [
      {
        field: 'agendaId',
        header: 'Agenda ID',
        type: 'text'
      },
      {
        field: 'subject',
        header: 'Subject',
        type: 'text'
      },
      {
        field: 'meeting',
        header: 'Meeting',
        type: 'link'
      },
      {
        field: 'linkedEntity',
        header: 'Linked PR',
        type: 'link'
      },
      {
        field: 'stage',
        header: 'Stage',
        type: 'badge',
        badgeClass: 'status-badge',
        badgeColor: (row: any) => this.getStageBadgeColor(row.stage)
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'recommendation-badge'
      },
      {
        field: 'createdBy',
        header: 'Created by',
        type: 'text'
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        actions: actionIcons
      }
    ];
  }

  async fetchAgendaItems(): Promise<void> {
    // Prevent duplicate calls
    if (this.isFetching) {
      console.log('fetchAgendaItems already in progress, skipping duplicate call');
      return;
    }
    
    this.isFetching = true;
    this._loaderService.showLoader();
    
    try {
      const url = `${environment.API_URL}/api/AgendaItems`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));
      
      if (response?.isSuccess && response?.data) {
        this.agendaItems = response.data.map((item: any) => {
          // Only show the first PR identifier, not all of them concatenated
          const linkedPR = item.prIdentifiers && Array.isArray(item.prIdentifiers) && item.prIdentifiers.length > 0 
            ? item.prIdentifiers[0] 
            : 'N/A';
          
          // Use meetingId from API response (string MeetingId from Meeting entity)
          const meetingId = item.meetingId || 'N/A';
          
          return {
            id: item.id,
            agendaId: `A-${item.id.toString().padStart(3, '0')}`,
            subject: item.subject,
            meeting: meetingId,
            linkedEntity: linkedPR,
            stage: item.currentStage || 'N/A',
            status: item.statusName || 'Open',
            statusValue: item.status || 1, // Store the enum value for checking if completed
            createdBy: item.createdBy || 'System',
            createdDate: item.createdAt
          };
        });
        // Trigger change detection to ensure data is rendered
        this.cdr.markForCheck();
      } else {
        console.error('Failed to fetch agenda items:', response?.errorMessageEn);
        this.agendaItems = [];
        this.cdr.markForCheck();
      }
    } catch (error) {
      console.error('Failed to fetch agenda items:', error);
      this.agendaItems = [];
      this.cdr.markForCheck();
    } finally {
      this.isFetching = false;
      this._loaderService.hideLoader();
      // Ensure change detection runs after loader is hidden
      this.cdr.markForCheck();
    }
  }

  getStageBadgeColor(stage: any): 'green' | 'blue' | 'pink' | 'gray' | 'yellow' | 'red' | 'orange' | 'purple' {
    const value = (stage || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (value.includes('unsealingtechnical')) return 'blue';
    if (value.includes('unsealingcommercial')) return 'green';
    if (value.includes('unsealingcombined')) return 'orange';
    if (!value || value === 'na') return 'gray';
    return 'purple';
  }

  createAgenda(): void {
    const dialogRef = this.dialog.open(CreateAgendaItemDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'create-agenda-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Clear cache to ensure fresh data after creating new agenda item
        this.meetingsCache = null;
        // Refresh the list if agenda item was created
        this.fetchAgendaItems();
      }
    });
  }

  uploadAgendaItems(): void {
    // TODO: Implement upload functionality
    console.log('Upload Agenda Items');
  }

  onTableActionClick(event: {action: string, row: any}): void {
    switch(event.action) {
      case 'view':
        this.viewAgendaItem(event.row.agendaId);
        break;
      case 'edit':
        // Check if agenda item is completed - prevent editing
        if (event.row.statusValue === 3 || event.row.status?.toLowerCase() === 'completed') {
          this.toasterService.showError('Cannot edit a completed agenda item.', 'Edit Not Allowed');
          return;
        }
        this.editAgendaItem(event.row);
        break;
    }
  }

  onTableLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'meeting') {
      this.viewMeeting(event.row.meeting);
    } else if (event.field === 'linkedEntity') {
      this.viewLinkedEntity(event.row.linkedEntity);
    }
  }

  viewAgendaItem(agendaId: string): void {
    // Extract the numeric ID from agendaId (format: A-001)
    const numericId = parseInt(agendaId.replace('A-', ''), 10);
    if (isNaN(numericId)) {
      console.error('Invalid agenda ID format:', agendaId);
      return;
    }

    // Open the dialog in view mode
    const dialogRef = this.dialog.open(CreateAgendaItemDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'create-agenda-dialog',
      data: {
        viewMode: true,
        agendaItemId: numericId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      // No action needed for view mode
    });
  }

  editAgendaItem(row: any): void {
    // Find the full agenda item data
    const agendaItem = this.agendaItems.find(item => item.agendaId === row.agendaId);
    if (!agendaItem) {
      console.error('Agenda item not found:', row.agendaId);
      return;
    }

    // Extract the PR number from linkedEntity
    const prNumber = row.linkedEntity && row.linkedEntity !== 'N/A' ? row.linkedEntity : null;
    
    // Open the dialog in edit mode
    const dialogRef = this.dialog.open(CreateAgendaItemDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'create-agenda-dialog',
      data: {
        editMode: true,
        agendaItemId: agendaItem.id,
        prNumber: prNumber,
        currentStage: row.status || ''
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        // Clear cache to ensure fresh data after editing agenda item
        this.meetingsCache = null;
        // Refresh the list if agenda item was updated
        this.fetchAgendaItems();
      }
    });
  }

  viewMeeting(meetingId: string): void {
    if (meetingId && meetingId !== 'N/A') {
      // Navigate to meeting details page using the meetingId string
      this._router.navigate([`/${AppConstants.ROUTES.MEETINGS}/${AppConstants.ROUTES.VIEW_MEETING}/${meetingId}`]);
    }
  }

  viewLinkedEntity(prNumber: string): void {
    if (prNumber && prNumber !== 'N/A') {
      // Navigate to PR details page using the PR number
      this._router.navigate(['/', AppConstants.ROUTES.APPROVED_PRS, AppConstants.ROUTES.PRDETAILS, prNumber]);
    }
  }
}

